/**
 * CarrierNetworkDetail - Detail view for drilling into carrier network data
 *
 * Features:
 * - All carriers in network
 * - Relationship strength visualization
 * - Shared attributes (vehicles, drivers, addresses)
 * - Timeline of connections
 * - VIN sharing analysis
 * - Shell game indicators
 */

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Sankey,
  Treemap,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import {
  Network,
  Truck,
  Users,
  MapPin,
  AlertTriangle,
  Link,
  Clock,
  Shield,
  Building2,
  Activity,
} from 'lucide-react';
import { useDrillDown } from '../../contexts/DrillDownContext';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface Carrier {
  dot_number?: string;
  dotNumber?: string;  // CamelCase alias
  legal_name?: string;
  legalName?: string;  // CamelCase alias
}

interface RelatedCarrier {
  dotNumber: string;
  legalName: string;
  connectionType: string;
  connectionStrength: number;
  sharedCount?: number;
  dot_number?: string;
  dot?: string;  // Alternative alias for DOT number
  legal_name?: string;
  name?: string;
  strength?: number;
  connection_type?: string;
  risk_level?: 'high' | 'medium' | 'low';
}

interface SharedVehicle {
  vin: string;
  unitType?: string;
  carriers: string[];
  firstSeen?: string;
  lastSeen?: string;
  make?: string;
  carrier_count?: number;
  last_seen?: string;
}

interface SharedAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
  carriers: string[];
  addressType: 'mailing' | 'physical' | 'both';
}

interface ConnectionTimelineEvent {
  date: string;
  eventType: 'connected' | 'disconnected' | 'changed';
  carrierDot: string;
  carrierName: string;
  connectionType: string;
  details?: string;
}

interface NetworkNode {
  id: string;
  type: 'carrier' | 'vehicle' | 'address' | 'contact';
  label: string;
  x?: number;
  y?: number;
  size?: number;
  color?: string;
  data?: Record<string, unknown>;
}

interface CarrierNetworkDetailProps {
  data: {
    carrier?: Carrier;
    dot?: string;
    name?: string;
    violations?: any[];
    crashes?: any[];
    inspections?: any[];
    relatedCarriers?: RelatedCarrier[];
    sharedVehicles?: SharedVehicle[];
    sharedAddresses?: SharedAddress[];
    connectionTimeline?: ConnectionTimelineEvent[];
    networkNodes?: NetworkNode[];
    networkLinks?: any[];
    state?: string;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CHART_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#84cc16', '#14b8a6',
];

const RISK_COLORS = {
  critical: '#dc2626',
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
};

// ============================================================================
// COMPONENT
// ============================================================================

const CarrierNetworkDetail: React.FC<CarrierNetworkDetailProps> = ({ data }) => {
  const { drillDown } = useDrillDown();

  const carrierName = data?.name || data?.carrier?.legalName || 'Unknown Carrier';
  const dotNumber = data?.dot || data?.carrier?.dotNumber || 'Unknown';
  const violations = data?.violations || [];
  const crashes = data?.crashes || [];
  const inspections = data?.inspections || [];
  const relatedCarriers = data?.relatedCarriers || [];
  const sharedVehicles = data?.sharedVehicles || [];

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalViolations = violations.length;
    const totalCrashes = crashes.length;
    const totalInspections = inspections.length;
    const oosViolations = violations.filter(v => v.outOfServiceIndicator === 'Y').length;
    const relatedCount = relatedCarriers.length;
    const sharedVINCount = sharedVehicles.length;

    return {
      totalViolations,
      totalCrashes,
      totalInspections,
      oosViolations,
      oosRate: totalViolations > 0 ? ((oosViolations / totalViolations) * 100).toFixed(1) : '0',
      violationRate: totalInspections > 0
        ? ((totalViolations / totalInspections) * 100).toFixed(1)
        : '0',
      relatedCount,
      sharedVINCount,
    };
  }, [violations, crashes, inspections, relatedCarriers, sharedVehicles]);

  // BASIC category distribution
  const basicDistribution = useMemo(() => {
    const categoryMap = new Map<string, number>();

    violations.forEach(v => {
      const category = v.basicCategory || 'Unknown';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    return Array.from(categoryMap.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);
  }, [violations]);

  // Timeline trend
  const timelineTrend = useMemo(() => {
    const monthMap = new Map<string, { violations: number; inspections: number }>();

    violations.forEach(v => {
      const dateStr = v.inspDate || v.inspectionDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthMap.has(key)) {
          monthMap.set(key, { violations: 0, inspections: 0 });
        }
        monthMap.get(key)!.violations++;
      }
    });

    inspections.forEach(i => {
      const dateStr = i.inspDate || i.inspectionDate;
      if (dateStr) {
        const date = new Date(dateStr);
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthMap.has(key)) {
          monthMap.set(key, { violations: 0, inspections: 0 });
        }
        monthMap.get(key)!.inspections++;
      }
    });

    return Array.from(monthMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-12)
      .map(([month, stats]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        ...stats,
      }));
  }, [violations, inspections]);

  // Geographic distribution
  const geographicDistribution = useMemo(() => {
    const stateMap = new Map<string, number>();

    violations.forEach(v => {
      const state = v.report_state || v.state || 'Unknown';
      stateMap.set(state, (stateMap.get(state) || 0) + 1);
    });

    return Array.from(stateMap.entries())
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [violations]);

  // Calculate carrier risk score
  const riskAssessment = useMemo(() => {
    const baseScore = 50; // Start with neutral score
    let score = baseScore;

    // Adjust based on violation rate
    const violationRate = summaryStats.totalInspections > 0
      ? (summaryStats.totalViolations / summaryStats.totalInspections) * 100
      : 0;

    if (violationRate > 50) score += 30;
    else if (violationRate > 30) score += 20;
    else if (violationRate > 15) score += 10;

    // Adjust based on OOS rate
    const oosRate = parseFloat(summaryStats.oosRate);
    if (oosRate > 30) score += 20;
    else if (oosRate > 20) score += 10;
    else if (oosRate > 10) score += 5;

    // Adjust based on crashes
    if (summaryStats.totalCrashes > 5) score += 15;
    else if (summaryStats.totalCrashes > 2) score += 10;
    else if (summaryStats.totalCrashes > 0) score += 5;

    // Adjust based on related carriers (shell game indicator)
    if (summaryStats.relatedCount > 5) score += 15;
    else if (summaryStats.relatedCount > 2) score += 10;

    // Adjust based on shared VINs
    if (summaryStats.sharedVINCount > 10) score += 15;
    else if (summaryStats.sharedVINCount > 5) score += 10;

    const finalScore = Math.min(100, Math.max(0, score));
    let level: 'critical' | 'high' | 'medium' | 'low' = 'low';
    if (finalScore >= 80) level = 'critical';
    else if (finalScore >= 60) level = 'high';
    else if (finalScore >= 40) level = 'medium';

    return { score: finalScore, level };
  }, [summaryStats]);

  // Handle related carrier click
  const handleRelatedCarrierClick = (carrier: RelatedCarrier) => {
    drillDown('carrier-detail', carrier.legalName || carrier.dotNumber, {
      carrier,
      dot: carrier.dotNumber,
      name: carrier.legalName,
    });
  };

  // Handle category click
  const handleCategoryClick = (category: string) => {
    const categoryViolations = violations.filter(v => v.basicCategory === category);
    drillDown('violation-category', category, {
      category,
      violations: categoryViolations,
      carrier: carrierName,
      dot: dotNumber,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Carrier Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        padding: '1rem',
        backgroundColor: `${RISK_COLORS[riskAssessment.level]}15`,
        borderLeft: `4px solid ${RISK_COLORS[riskAssessment.level]}`,
        borderRadius: '8px',
      }}>
        <Truck size={32} style={{ color: RISK_COLORS[riskAssessment.level], flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>
            {carrierName}
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: '0.25rem 0' }}>
            DOT: {dotNumber}
            {data?.state && ` | State: ${data.state}`}
          </p>
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0.5rem 1rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: RISK_COLORS[riskAssessment.level],
          }}>
            {riskAssessment.score}
          </div>
          <div style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: RISK_COLORS[riskAssessment.level],
          }}>
            Risk Score
          </div>
          <div style={{
            marginTop: '0.25rem',
            padding: '0.125rem 0.5rem',
            backgroundColor: RISK_COLORS[riskAssessment.level],
            color: 'white',
            fontSize: '0.625rem',
            fontWeight: 600,
            borderRadius: '4px',
            textTransform: 'uppercase',
          }}>
            {riskAssessment.level}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="drill-down-stats-row">
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.totalInspections.toLocaleString()}</div>
          <div className="drill-down-stat-label">Inspections</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-warning)' }}>
            {summaryStats.totalViolations.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">Violations</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
            {summaryStats.totalCrashes.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">Crashes</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.oosRate}%</div>
          <div className="drill-down-stat-label">OOS Rate</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-primary)' }}>
            {summaryStats.relatedCount}
          </div>
          <div className="drill-down-stat-label">Related Carriers</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-info)' }}>
            {summaryStats.sharedVINCount}
          </div>
          <div className="drill-down-stat-label">Shared VINs</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="drill-down-detail-grid">
        {/* BASIC Categories */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Shield size={18} style={{ color: 'var(--color-warning)' }} />
              Violation Categories
            </h4>
          </div>
          {basicDistribution.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={basicDistribution}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={(props: PieLabelRenderProps) => typeof props.percent === 'number' ? `${(props.percent * 100).toFixed(0)}%` : ''}
                    onClick={(data: { category: string }) => handleCategoryClick(data.category)}
                    cursor="pointer"
                  >
                    {basicDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ fontSize: '0.8125rem' }}
                    formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No violation data available
            </div>
          )}
        </div>

        {/* Geographic Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <MapPin size={18} style={{ color: 'var(--color-success)' }} />
              States of Operation
            </h4>
          </div>
          {geographicDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={geographicDistribution} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="state" tick={{ fontSize: 11 }} width={35} />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Bar dataKey="count" fill="#10b981" name="Violations" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No geographic data available
            </div>
          )}
        </div>

        {/* Timeline Trend */}
        <div className="drill-down-detail-card" style={{ gridColumn: 'span 2' }}>
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Activity size={18} style={{ color: 'var(--color-primary)' }} />
              Activity Timeline
            </h4>
            <span className="drill-down-detail-card-badge">
              12 Months
            </span>
          </div>
          {timelineTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={timelineTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Line
                  type="monotone"
                  dataKey="violations"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  name="Violations"
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="inspections"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="Inspections"
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No timeline data available
            </div>
          )}
        </div>
      </div>

      {/* Related Carriers */}
      {relatedCarriers.length > 0 && (
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Network size={18} style={{ color: 'var(--color-danger)' }} />
              Related Carriers (Network)
            </h4>
            <span className="drill-down-detail-card-badge" style={{ backgroundColor: 'var(--color-danger)' }}>
              {relatedCarriers.length} connections
            </span>
          </div>
          <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
            Carriers connected through shared vehicles, addresses, or other indicators
          </p>
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: 'white' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>DOT</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Connection Type</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Strength</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {relatedCarriers.slice(0, 20).map((carrier, idx) => (
                  <tr
                    key={idx}
                    style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                    onClick={() => handleRelatedCarrierClick(carrier)}
                    className="chart-clickable"
                  >
                    <td style={{ padding: '0.75rem', fontFamily: 'monospace' }}>
                      {carrier.dotNumber || carrier.dot || '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {carrier.legalName || carrier.name || '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        color: 'var(--color-primary)',
                        fontSize: '0.6875rem',
                        borderRadius: '4px',
                      }}>
                        <Link size={10} />
                        {carrier.connection_type || 'Shared VIN'}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <div style={{
                        width: '60px',
                        height: '6px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '3px',
                        overflow: 'hidden',
                        margin: '0 auto',
                      }}>
                        <div style={{
                          width: `${carrier.strength || 50}%`,
                          height: '100%',
                          backgroundColor: (carrier.strength || 50) > 70 ? 'var(--color-danger)' :
                                          (carrier.strength || 50) > 40 ? 'var(--color-warning)' :
                                          'var(--color-success)',
                        }} />
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        backgroundColor: carrier.risk_level === 'high' ? 'rgba(239, 68, 68, 0.1)' :
                                        carrier.risk_level === 'medium' ? 'rgba(245, 158, 11, 0.1)' :
                                        'rgba(16, 185, 129, 0.1)',
                        color: carrier.risk_level === 'high' ? 'var(--color-danger)' :
                               carrier.risk_level === 'medium' ? 'var(--color-warning)' :
                               'var(--color-success)',
                      }}>
                        {carrier.risk_level || 'unknown'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Shared Vehicles */}
      {sharedVehicles.length > 0 && (
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Truck size={18} style={{ color: 'var(--color-warning)' }} />
              Shared Vehicles (Shell Game Indicator)
            </h4>
            <span className="drill-down-detail-card-badge" style={{ backgroundColor: 'var(--color-warning)' }}>
              {sharedVehicles.length} VINs
            </span>
          </div>
          <div style={{ maxHeight: '250px', overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: 'white' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>VIN</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Make</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Carriers</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {sharedVehicles.slice(0, 15).map((vehicle, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {vehicle.vin || '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {vehicle.make || '-'}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: (vehicle.carrier_count || 0) > 3 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                        color: (vehicle.carrier_count || 0) > 3 ? 'var(--color-danger)' : 'var(--color-warning)',
                        borderRadius: '4px',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}>
                        {vehicle.carrier_count || 2}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {vehicle.last_seen ? new Date(vehicle.last_seen).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Safety Alert */}
      {riskAssessment.level === 'critical' || riskAssessment.level === 'high' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '1rem',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
        }}>
          <AlertTriangle size={24} style={{ color: 'var(--color-danger)', flexShrink: 0 }} />
          <div>
            <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0, color: 'var(--color-danger)' }}>
              High Risk Carrier Alert
            </h4>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
              This carrier has elevated risk indicators including high violation rates, crash history,
              {summaryStats.relatedCount > 0 && ` connections to ${summaryStats.relatedCount} other carriers,`}
              {summaryStats.sharedVINCount > 0 && ` ${summaryStats.sharedVINCount} shared vehicles`}.
              Consider enhanced monitoring and investigation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierNetworkDetail;
